/**
 * MagicLinkFlow.js
 * M-05: Complete public flow for NOM-035 evaluation without login.
 * Reuses Guide I (4 ATS questions) and Guide III (72 risk factor questions).
 * State machine: Identify → Consent → Guide I → Guide III → Complete
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Shield, User, Hash, ArrowRight, CheckCircle, AlertTriangle, Lock,
  ClipboardList, ChevronLeft, ChevronRight, Loader2
} from 'lucide-react';

const TEXTS = {
  es: {
    pageTitle: 'Evaluación NOM-035-STPS-2018',
    pageSubtitle: 'Evaluación de factores de riesgo psicosocial',
    confidential: 'Esta evaluación es voluntaria y confidencial. Tus respuestas individuales no serán compartidas con tu empleador.',
    idLabel: 'Número de nómina / empleado',
    idPlaceholder: 'Ej: GD-0001',
    nameLabel: 'Nombre (como aparece en nómina)',
    namePlaceholder: 'Ej: Carlos',
    submit: 'Validar identidad',
    validating: 'Validando...',
    errorNeutral: 'No pudimos validar tus datos, verifica con tu área de RH.',
    errorRateLimit: 'Hemos detectado actividad inusual. Por favor, inténtalo de nuevo más tarde.',
    errorInvalidLink: 'Este enlace no es válido o ha expirado.',
    noCycle: 'No hay una evaluación activa para tu centro en este momento.',
    welcome: 'Bienvenido/a',
    center: 'Centro de trabajo',
    consentTitle: 'Consentimiento Informado',
    consentText: 'Al continuar, acepto participar voluntariamente en esta evaluación de factores de riesgo psicosocial conforme a la NOM-035-STPS-2018. Entiendo que mis respuestas son confidenciales y serán utilizadas exclusivamente para fines de diagnóstico y mejora del entorno organizacional. Los resultados se presentan de forma agregada, sin identificar respuestas individuales.',
    consentAccept: 'Acepto y continúo',
    consentDecline: 'No deseo participar',
    guide1Title: 'Guía I — Identificación de Acontecimientos Traumáticos Severos',
    guide1Desc: 'Cuestionario de 4 preguntas sobre experiencias en el trabajo.',
    guide3Title: 'Guía III — Factores de Riesgo Psicosocial',
    guide3Desc: 'Evaluación de condiciones del entorno de trabajo.',
    startGuide: 'Comenzar',
    resume: 'Continuar donde te quedaste',
    completed: '¡Completada!',
    allDone: 'Tu evaluación está completa. ¡Gracias por participar!',
    allDoneDesc: 'Tus respuestas han sido registradas de forma confidencial. Puedes cerrar esta ventana.',
    yes: 'Sí',
    no: 'No',
    previous: 'Anterior',
    next: 'Siguiente',
    finish: 'Finalizar',
    progress: 'Progreso',
    saving: 'Guardando...',
    section: 'Sección'
  },
  en: {
    pageTitle: 'NOM-035-STPS-2018 Evaluation',
    pageSubtitle: 'Psychosocial risk factor assessment',
    confidential: 'This evaluation is voluntary and confidential. Your individual responses will not be shared with your employer.',
    idLabel: 'Employee / payroll number',
    idPlaceholder: 'E.g.: GD-0001',
    nameLabel: 'Name (as on payroll)',
    namePlaceholder: 'E.g.: Carlos',
    submit: 'Validate identity',
    validating: 'Validating...',
    errorNeutral: 'We couldn\'t validate your data. Please check with your HR department.',
    errorRateLimit: 'Unusual activity detected. Please try again later.',
    errorInvalidLink: 'This link is invalid or has expired.',
    noCycle: 'There is no active evaluation for your center at this time.',
    welcome: 'Welcome',
    center: 'Work center',
    consentTitle: 'Informed Consent',
    consentText: 'By continuing, I agree to voluntarily participate in this psychosocial risk factor evaluation per NOM-035-STPS-2018. I understand that my responses are confidential and will be used exclusively for organizational improvement purposes. Results are presented in aggregate form without identifying individual responses.',
    consentAccept: 'I accept and continue',
    consentDecline: 'I don\'t wish to participate',
    guide1Title: 'Guide I — Severe Traumatic Events Identification',
    guide1Desc: '4-question survey about workplace experiences.',
    guide3Title: 'Guide III — Psychosocial Risk Factors',
    guide3Desc: 'Evaluation of work environment conditions.',
    startGuide: 'Start',
    resume: 'Continue where you left off',
    completed: 'Completed!',
    allDone: 'Your evaluation is complete. Thank you for participating!',
    allDoneDesc: 'Your responses have been recorded confidentially. You may close this window.',
    yes: 'Yes',
    no: 'No',
    previous: 'Previous',
    next: 'Next',
    finish: 'Finish',
    progress: 'Progress',
    saving: 'Saving...',
    section: 'Section'
  }
};

// Guide I questions (ATS) — reused from NOM035EmployeePanel
const GUIDE1_QUESTIONS = [
  { id: 1, text_es: '¿Ha presenciado o sufrido alguna vez, durante o con motivo del trabajo un acontecimiento que le haya causado mucho miedo o que le provocara un estado de shock?', text_en: 'Have you ever witnessed or suffered, during or because of work, an event that caused you great fear or shock?' },
  { id: 2, text_es: '¿Ha sido amenazado o agredido físicamente durante su trabajo?', text_en: 'Have you been threatened or physically assaulted during your work?' },
  { id: 3, text_es: '¿Ha sufrido algún accidente grave de trabajo?', text_en: 'Have you suffered a serious work accident?' },
  { id: 4, text_es: '¿Ha visto morir o ha muerto algún compañero durante el trabajo?', text_en: 'Have you seen a coworker die during work?' }
];

const MagicLinkFlow = () => {
  const { token } = useParams();
  const [lang, setLang] = useState('es');
  const t = TEXTS[lang];

  // Flow states: 'identify' | 'consent' | 'guide_hub' | 'guide1' | 'guide3' | 'all_done' | 'error' | 'no_cycle'
  const [step, setStep] = useState('identify');
  const [error, setError] = useState('');

  // Identity
  const [numeroEmpleado, setNumeroEmpleado] = useState('');
  const [nombre, setNombre] = useState('');
  const [validating, setValidating] = useState(false);

  // Resolved employee data
  const [employee, setEmployee] = useState(null);
  const [centro, setCentro] = useState(null);
  const [ciclo, setCiclo] = useState(null);

  // Guide states
  const [guide1Status, setGuide1Status] = useState('not_started'); // not_started | in_progress | completed
  const [guide3Status, setGuide3Status] = useState('not_started');
  const [guide1Answers, setGuide1Answers] = useState({});
  const [guide3Data, setGuide3Data] = useState(null); // questionnaire structure
  const [guide3Answers, setGuide3Answers] = useState({});
  const [guide3DomainIdx, setGuide3DomainIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);

  // §3.2: Validate identity
  const handleValidate = async () => {
    if (!numeroEmpleado.trim() || !nombre.trim()) {
      toast.error(lang === 'es' ? 'Completa ambos campos' : 'Fill in both fields');
      return;
    }
    setValidating(true);
    setError('');
    try {
      const res = await axios.post(`/v5/nom035/magic/${token}/validate`, {
        numero_empleado: numeroEmpleado.trim(),
        nombre: nombre.trim()
      });
      if (res.data?.success) {
        const d = res.data.data;
        setEmployee(d.employee);
        setCentro(d.centro);
        if (!d.ciclo_activo) {
          setStep('no_cycle');
          return;
        }
        setCiclo(d.ciclo_activo);
        // Resolve initial states
        if (d.ciclo_activo.guia1_completada) setGuide1Status('completed');
        if (d.ciclo_activo.guia3_completada) setGuide3Status('completed');
        // Load progress
        await loadProgress(d.employee.user_id, d.ciclo_activo);
        // Check if all done
        if (d.ciclo_activo.guia1_completada && d.ciclo_activo.guia3_completada) {
          setStep('all_done');
        } else {
          setStep('consent');
        }
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setError(t.errorRateLimit);
      } else if (status === 404) {
        const detail = err.response?.data?.detail || '';
        if (detail.includes('inválido') || detail.includes('expirado') || detail.includes('invalid')) {
          setError(t.errorInvalidLink);
        } else {
          setError(t.errorNeutral);
        }
      } else {
        setError(t.errorNeutral);
      }
    } finally {
      setValidating(false);
    }
  };

  // Load progress from D-03
  const loadProgress = async (userId, cicloData) => {
    try {
      if (!cicloData.guia1_completada) {
        const g1 = await axios.get(`/v5/nom035/magic/${token}/progress/${userId}/I`);
        if (g1.data?.data?.respuestas && Object.keys(g1.data.data.respuestas).length > 0) {
          setGuide1Answers(g1.data.data.respuestas);
          setGuide1Status('in_progress');
        }
      }
      if (!cicloData.guia3_completada) {
        const g3 = await axios.get(`/v5/nom035/magic/${token}/progress/${userId}/III`);
        if (g3.data?.data?.respuestas && Object.keys(g3.data.data.respuestas).length > 0) {
          setGuide3Answers(g3.data.data.respuestas);
          setGuide3Status('in_progress');
        }
      }
    } catch { /* silent */ }
  };

  // §3.4: Consent
  const handleConsent = async () => {
    // Record consent timestamp via progress
    try {
      await axios.put(`/v5/nom035/magic/${token}/progress/${employee.user_id}/CONSENT`, {
        respuestas: { accepted: true, timestamp: new Date().toISOString() },
        completado: true
      });
    } catch { /* non-blocking */ }
    setStep('guide_hub');
  };

  // Autosave progress
  const saveProgress = useCallback(async (guia, answers, completado = false) => {
    if (!employee) return;
    setSaving(true);
    try {
      await axios.put(`/v5/nom035/magic/${token}/progress/${employee.user_id}/${guia}`, {
        respuestas: answers,
        completado
      });
    } catch { /* silent */ }
    finally { setSaving(false); }
  }, [employee, token]);

  // §3.5 Guide I completion
  const submitGuide1 = async () => {
    setSubmitting(true);
    try {
      const tieneAts = Object.values(guide1Answers).some(a => a === 'SI');
      await axios.post(`/v5/nom035/${ciclo.config_id}/guide1/employee-response-magic`, {
        user_id: employee.user_id,
        token: token,
        respuestas: guide1Answers,
        tiene_ats: tieneAts
      });
      setGuide1Status('completed');
      await saveProgress('I', guide1Answers, true);
      toast.success(lang === 'es' ? 'Guía I completada' : 'Guide I completed');
      // Check if all done now
      if (guide3Status === 'completed') {
        setStep('all_done');
      } else {
        setStep('guide_hub');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  // Load Guide III questionnaire
  const loadGuide3Questionnaire = async () => {
    try {
      const configId = ciclo.config_id;
      const res = await axios.get(`/v5/nom035/magic/${token}/guide3-questionnaire/${configId}`);
      if (res.data?.success) {
        setGuide3Data(res.data.data);
      }
    } catch (err) {
      toast.error(lang === 'es' ? 'Error cargando cuestionario' : 'Error loading questionnaire');
    }
  };

  // §3.5 Guide III completion
  const submitGuide3 = async () => {
    setSubmitting(true);
    try {
      await axios.post(`/v5/nom035/${ciclo.config_id}/guide3/employee-response-magic`, {
        user_id: employee.user_id,
        token: token,
        respuestas: guide3Answers
      });
      setGuide3Status('completed');
      await saveProgress('III', guide3Answers, true);
      toast.success(lang === 'es' ? 'Guía III completada' : 'Guide III completed');
      if (guide1Status === 'completed') {
        setStep('all_done');
      } else {
        setStep('guide_hub');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  // Autosave on answer changes for guide3
  useEffect(() => {
    if (step === 'guide3' && Object.keys(guide3Answers).length > 0) {
      const timer = setTimeout(() => saveProgress('III', guide3Answers), 3000);
      return () => clearTimeout(timer);
    }
  }, [guide3Answers, step, saveProgress]);

  // ══════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{t.pageTitle}</h1>
              <p className="text-xs text-gray-500">{t.pageSubtitle}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setLang(l => l === 'es' ? 'en' : 'es')}>
            {lang === 'es' ? 'EN' : 'ES'}
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">

        {/* ═══ STEP: IDENTIFY ═══ */}
        {step === 'identify' && (
          <Card className="border-t-4 border-t-teal-500" data-testid="magic-identify-card">
            <CardHeader className="text-center pb-3">
              <CardTitle className="text-xl">{t.pageTitle}</CardTitle>
              <CardDescription className="text-sm mt-2 max-w-md mx-auto">{t.confidential}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 max-w-sm mx-auto">
              <div>
                <Label htmlFor="numero_empleado" className="text-sm">{t.idLabel}</Label>
                <div className="relative mt-1">
                  <Hash className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input id="numero_empleado" className="pl-9" placeholder={t.idPlaceholder}
                    value={numeroEmpleado} onChange={e => setNumeroEmpleado(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleValidate()}
                    data-testid="magic-input-nomina" />
                </div>
              </div>
              <div>
                <Label htmlFor="nombre" className="text-sm">{t.nameLabel}</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <Input id="nombre" className="pl-9" placeholder={t.namePlaceholder}
                    value={nombre} onChange={e => setNombre(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleValidate()}
                    data-testid="magic-input-nombre" />
                </div>
              </div>
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2" data-testid="magic-error">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-center pb-6">
              <Button onClick={handleValidate} disabled={validating} className="bg-teal-600 hover:bg-teal-700 gap-2"
                data-testid="magic-validate-btn">
                {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {validating ? t.validating : t.submit}
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* ═══ STEP: NO CYCLE ═══ */}
        {step === 'no_cycle' && (
          <Card className="text-center" data-testid="magic-no-cycle">
            <CardContent className="py-12">
              <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">{t.welcome}, {employee?.nombre_completo}</h3>
              <p className="text-gray-600">{t.noCycle}</p>
            </CardContent>
          </Card>
        )}

        {/* ═══ STEP: CONSENT ═══ */}
        {step === 'consent' && (
          <Card className="border-t-4 border-t-purple-500" data-testid="magic-consent-card">
            <CardHeader className="text-center">
              <Lock className="h-8 w-8 mx-auto text-purple-600 mb-2" />
              <CardTitle>{t.consentTitle}</CardTitle>
              <CardDescription>{t.welcome}, {employee?.nombre_completo}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-gray-50 rounded-lg border text-sm text-gray-700 leading-relaxed mb-4">
                {t.consentText}
              </div>
              <p className="text-xs text-gray-400 text-center">
                {centro?.nombre && <><Badge variant="outline" className="text-xs">{t.center}: {centro.nombre}</Badge></>}
              </p>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="ghost" size="sm" onClick={() => setStep('identify')}>{t.consentDecline}</Button>
              <Button onClick={handleConsent} className="bg-purple-600 hover:bg-purple-700 gap-2"
                data-testid="magic-consent-accept">
                <CheckCircle className="h-4 w-4" />{t.consentAccept}
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* ═══ STEP: GUIDE HUB ═══ */}
        {step === 'guide_hub' && (
          <div className="space-y-4" data-testid="magic-guide-hub">
            <div className="text-center mb-6">
              <h2 className="text-lg font-semibold text-gray-800">{t.welcome}, {employee?.nombre_completo}</h2>
              <p className="text-sm text-gray-500">{centro?.nombre}</p>
            </div>

            {/* Guide I Card */}
            <Card className={`border-l-4 ${guide1Status === 'completed' ? 'border-l-green-500 bg-green-50/30' : 'border-l-blue-500'}`}>
              <CardContent className="py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${guide1Status === 'completed' ? 'bg-green-100' : 'bg-blue-100'}`}>
                    {guide1Status === 'completed' ? <CheckCircle className="h-5 w-5 text-green-600" /> : <ClipboardList className="h-5 w-5 text-blue-600" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{t.guide1Title}</p>
                    <p className="text-xs text-gray-500">{t.guide1Desc}</p>
                  </div>
                </div>
                {guide1Status === 'completed' ? (
                  <Badge className="bg-green-100 text-green-700">{t.completed}</Badge>
                ) : (
                  <Button size="sm" onClick={() => setStep('guide1')} className="bg-blue-600 hover:bg-blue-700"
                    data-testid="magic-start-guide1">
                    {guide1Status === 'in_progress' ? t.resume : t.startGuide}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Guide III Card */}
            <Card className={`border-l-4 ${guide3Status === 'completed' ? 'border-l-green-500 bg-green-50/30' : 'border-l-teal-500'}`}>
              <CardContent className="py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${guide3Status === 'completed' ? 'bg-green-100' : 'bg-teal-100'}`}>
                    {guide3Status === 'completed' ? <CheckCircle className="h-5 w-5 text-green-600" /> : <ClipboardList className="h-5 w-5 text-teal-600" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{t.guide3Title}</p>
                    <p className="text-xs text-gray-500">{t.guide3Desc}</p>
                  </div>
                </div>
                {guide3Status === 'completed' ? (
                  <Badge className="bg-green-100 text-green-700">{t.completed}</Badge>
                ) : (
                  <Button size="sm" onClick={() => { setStep('guide3'); loadGuide3Questionnaire(); }} className="bg-teal-600 hover:bg-teal-700"
                    data-testid="magic-start-guide3">
                    {guide3Status === 'in_progress' ? t.resume : t.startGuide}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ═══ STEP: GUIDE I (ATS) ═══ */}
        {step === 'guide1' && (
          <Card data-testid="magic-guide1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ClipboardList className="h-5 w-5 text-blue-600" />{t.guide1Title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {GUIDE1_QUESTIONS.map((q, idx) => (
                <div key={q.id} className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm mb-3">{idx + 1}. {lang === 'es' ? q.text_es : q.text_en}</p>
                  <div className="flex gap-3">
                    {['SI', 'NO'].map(opt => (
                      <Button key={opt} size="sm"
                        variant={guide1Answers[q.id] === opt ? 'default' : 'outline'}
                        className={guide1Answers[q.id] === opt ? 'bg-blue-600' : ''}
                        onClick={() => setGuide1Answers(prev => ({ ...prev, [q.id]: opt }))}>
                        {opt === 'SI' ? t.yes : t.no}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => setStep('guide_hub')}>
                <ChevronLeft className="h-4 w-4 mr-1" />{t.previous}
              </Button>
              <Button onClick={submitGuide1}
                disabled={!GUIDE1_QUESTIONS.every(q => guide1Answers[q.id]) || submitting}
                className="bg-blue-600 hover:bg-blue-700" data-testid="magic-submit-guide1">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t.finish}
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* ═══ STEP: GUIDE III ═══ */}
        {step === 'guide3' && (
          <Card data-testid="magic-guide3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ClipboardList className="h-5 w-5 text-teal-600" />{t.guide3Title}
              </CardTitle>
              {guide3Data && (
                <div className="space-y-1 mt-2">
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{t.progress}: {Object.keys(guide3Answers).length}/{guide3Data.total_items || 0}</span>
                    <span>{guide3Data.total_items ? Math.round(Object.keys(guide3Answers).length / guide3Data.total_items * 100) : 0}%</span>
                  </div>
                  <Progress value={guide3Data.total_items ? (Object.keys(guide3Answers).length / guide3Data.total_items * 100) : 0} className="h-2" />
                  {saving && <p className="text-xs text-teal-600 animate-pulse">{t.saving}</p>}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {!guide3Data ? (
                <div className="py-8 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-teal-600" /></div>
              ) : (
                <>
                  {/* Domain tabs */}
                  <div className="flex gap-1 overflow-x-auto pb-3 mb-4">
                    {guide3Data.dominios?.map((domain, idx) => {
                      const complete = domain.items.every(it => guide3Answers[it.id] !== undefined);
                      return (
                        <button key={domain.id} onClick={() => setGuide3DomainIdx(idx)}
                          className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                            idx === guide3DomainIdx ? 'bg-teal-600 text-white'
                              : complete ? 'bg-green-100 text-green-700 border border-green-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}>
                          {idx + 1}. {domain.nombre?.substring(0, 18)}
                        </button>
                      );
                    })}
                  </div>
                  {/* Current domain */}
                  {guide3Data.dominios?.[guide3DomainIdx] && (
                    <div className="space-y-4">
                      <h4 className="font-medium text-sm text-teal-700 border-b pb-2">
                        {t.section} {guide3DomainIdx + 1}: {guide3Data.dominios[guide3DomainIdx].nombre}
                      </h4>
                      {guide3Data.dominios[guide3DomainIdx].items.map((item, idx) => (
                        <div key={item.id} className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm mb-3">{idx + 1}. {item.texto}</p>
                          <div className="flex flex-wrap gap-2">
                            {guide3Data.escala.map(opt => (
                              <button key={opt.value} onClick={() => setGuide3Answers(prev => ({ ...prev, [item.id]: opt.value }))}
                                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                  guide3Answers[item.id] === opt.value
                                    ? 'bg-teal-600 text-white border-teal-600'
                                    : 'bg-white text-gray-600 border-gray-300 hover:border-teal-400'
                                }`}>
                                {lang === 'es' ? opt.label_es : opt.label_en}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => {
                if (guide3DomainIdx > 0) setGuide3DomainIdx(prev => prev - 1);
                else { saveProgress('III', guide3Answers); setStep('guide_hub'); }
              }}>
                <ChevronLeft className="h-4 w-4 mr-1" />{t.previous}
              </Button>
              {guide3Data && guide3DomainIdx < (guide3Data.dominios?.length || 0) - 1 ? (
                <Button onClick={() => setGuide3DomainIdx(prev => prev + 1)} className="bg-teal-600 hover:bg-teal-700">
                  {t.next}<ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={submitGuide3}
                  disabled={!guide3Data || Object.keys(guide3Answers).length < (guide3Data?.total_items || 999) || submitting}
                  className="bg-teal-600 hover:bg-teal-700" data-testid="magic-submit-guide3">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t.finish}
                </Button>
              )}
            </CardFooter>
          </Card>
        )}

        {/* ═══ STEP: ALL DONE ═══ */}
        {step === 'all_done' && (
          <Card className="text-center border-t-4 border-t-green-500" data-testid="magic-all-done">
            <CardContent className="py-12">
              <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
              <h3 className="text-xl font-semibold text-green-700 mb-2">{t.allDone}</h3>
              <p className="text-gray-600 mb-4">{t.allDoneDesc}</p>
              {employee && <p className="text-sm text-gray-400">{employee.nombre_completo} — {centro?.nombre}</p>}
            </CardContent>
          </Card>
        )}

        {/* ═══ STEP: ERROR (invalid link) ═══ */}
        {step === 'error' && (
          <Card className="text-center">
            <CardContent className="py-12">
              <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
              <h3 className="text-lg font-medium text-red-700 mb-2">{t.errorInvalidLink}</h3>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-xs text-gray-400">
        NOM-035-STPS-2018 · {lang === 'es' ? 'Evaluación confidencial' : 'Confidential evaluation'}
      </footer>
    </div>
  );
};

export default MagicLinkFlow;
